import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { AnimatePresence, motion } from 'motion/react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { uploadPaymentProof, uploadPaymentProofMySell } from '@/api/general';
import { InfoIcon, PlusIcon } from '@/components/icons';
import LoadingSpinner from '@/components/loading-spinner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MAX_IMAGE_SIZE, MAX_NAME_LENGTH } from '@/config';
import { IS_LANG_EN, PAYMENT_TYPE } from '@/constants/general';
import { cn } from '@/lib/utils';

// Define supported image formats
const SUPPORTED_IMAGE_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];

const TransitionImageUploadModal = NiceModal.create(
  ({ onConfirm, id, paymentType }: { onConfirm: () => void; id: string; paymentType: string }) => {
    const modal = useModal('transition-image-upload-modal');
    const { t } = useTranslation();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [base64Image, setBase64Image] = useState<string | null>(null);
    const [isImageUploaded, setIsImageUploaded] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isNameError, setIsNameError] = useState(false);
    const [nameErrorMessage, setNameErrorMessage] = useState('');
    const [isFileSizeError, setIsFileSizeError] = useState(false);
    const [isFileFormatError, setIsFileFormatError] = useState(false);
    const [name, setName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleClose = () => {
      setSelectedFile(null);
      setBase64Image(null);
      setIsImageUploaded(false);
      setName('');
      setIsNameError(false);
      setNameErrorMessage('');
      setIsFileSizeError(false);
      setIsFileFormatError(false);
      modal.hide();
      setTimeout(() => {
        modal.remove();
      }, 200);
    };

    const handleConfirm = async () => {
      if (!selectedFile || !base64Image || !name) return;
      if (IS_LANG_EN) {
        if (!validateNameEn(name)) return;
      } else {
        if (!validateNameZh(name)) return;
      }
      if (isSubmitting) return;

      setIsSubmitting(true);

      const params = {
        id: id,
        image: base64Image,
        name,
      };

      try {
        if (paymentType === PAYMENT_TYPE.MY_SELL) {
          await uploadPaymentProofMySell(params);
        } else {
          await uploadPaymentProof(params);
        }

        onConfirm?.();
        handleClose();
      } catch (error) {
        console.error('Error uploading payment proof:', error);
      } finally {
        setIsSubmitting(false);
      }
    };

    const convertToBase64 = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
      });
    };

    const stripBase64Prefix = (base64String: string): string => {
      const commaIndex = base64String.indexOf(',');
      if (commaIndex === -1) return base64String;
      return base64String.substring(commaIndex + 1);
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;

      if (files && files.length > 0) {
        const file = files[0];

        if (!SUPPORTED_IMAGE_FORMATS.includes(file.type)) {
          setIsFileFormatError(true);
          setSelectedFile(null);
          setBase64Image(null);
          event.target.value = '';
          toast.warning(t('unsupported_file_format'));
          return;
        }
        setIsFileFormatError(false);

        if (file.size > MAX_IMAGE_SIZE) {
          setIsFileSizeError(true);
          setIsFileFormatError(false);
          setSelectedFile(null);
          setBase64Image(null);
          event.target.value = '';
          toast.warning(t('tran_image_size_limit'));
          return;
        }
        setIsFileSizeError(false);
        setSelectedFile(file);

        try {
          const base64 = await convertToBase64(file);
          setBase64Image(stripBase64Prefix(base64));
          setIsImageUploaded(true);
        } catch (error) {
          console.error('Error converting file to base64:', error);
        }
      }
    };

    const validateNameEn = (value: string) => {
      if (value.length < 2 || value.length > 50 || value.trim() === '') {
        setIsNameError(true);
        setNameErrorMessage(t('transfer_name_english_error'));
        return false;
      }

      if (!/^[a-zA-Z]/.test(value) || !/[a-zA-Z]$/.test(value)) {
        setIsNameError(true);
        setNameErrorMessage(t('transfer_name_english_error'));
        return false;
      }

      if (!/^[a-zA-Z ,.'-]+$/.test(value)) {
        setIsNameError(true);
        setNameErrorMessage(t('transfer_name_english_error'));
        return false;
      }

      if (/  +/.test(value)) {
        setIsNameError(true);
        setNameErrorMessage(t('transfer_name_english_error'));
        return false;
      }

      if (/[,.'-]{2,}/.test(value)) {
        setIsNameError(true);
        setNameErrorMessage(t('transfer_name_english_error'));
        return false;
      }

      if (/[,.'\- ] [,.'\- ]/.test(value)) {
        setIsNameError(true);
        setNameErrorMessage(t('transfer_name_english_error'));
        return false;
      }

      setIsNameError(false);
      setNameErrorMessage('');
      return true;
    };

    const validateNameZh = (value: string) => {
      if (value.length < 2 || value.length > 50 || value.trim() === '') {
        setIsNameError(true);
        setNameErrorMessage(t('transfer_name_chinese_error'));
        return false;
      }

      const isValidChinese = /^[\u4e00-\u9fa5\u00B7]+$/.test(value);

      if (isValidChinese) {
        const hasMiddleDotAtBoundary = value.startsWith('\u00B7') || value.endsWith('\u00B7');
        const hasConsecutiveMiddleDots = value.includes('\u00B7\u00B7');

        if (hasMiddleDotAtBoundary) {
          setIsNameError(true);
          setNameErrorMessage(t('transfer_name_chinese_error'));
          return false;
        }

        if (hasConsecutiveMiddleDots) {
          setIsNameError(true);
          setNameErrorMessage(t('transfer_name_chinese_error'));
          return false;
        }
      } else {
        setIsNameError(true);
        setNameErrorMessage(t('transfer_name_chinese_error'));
        return false;
      }

      setIsNameError(false);
      setNameErrorMessage('');
      return true;
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value.length > MAX_NAME_LENGTH) return;

      setName(value);
      if (IS_LANG_EN) validateNameEn(value);
      else validateNameZh(value);
    };

    return (
      <Dialog open={modal.visible}>
        <DialogContent className="w-[90%] max-w-[340px]">
          <DialogHeader>
            <DialogTitle className="text-center text-base pt-2 pb-3 font-medium">{t('tran_upload_image')}</DialogTitle>
          </DialogHeader>
          <div className="mb-3">
            {!isImageUploaded ? (
              <div>{t('tran_upload_image_hint_1')}</div>
            ) : (
              <div className="max-h-[360px] min-w-[200px] overflow-scroll">
                <div className="flex flex-col items-center gap-8">
                  <div className="grid w-full max-w-sm items-center gap-1.5 relative">
                    <Label htmlFor="name" className="gap-0.5">
                      <span className="text-caution">*</span>
                      {t('transfer_name')}
                    </Label>
                    <Input
                      isError={isNameError}
                      type="text"
                      id="name"
                      value={name}
                      onChange={handleNameChange}
                      placeholder={t('transfer_name_placeholder')}
                    />
                    {isNameError && (
                      <span className="absolute text-caution -bottom-5 pl-1.5 text-xs">{nameErrorMessage}</span>
                    )}
                  </div>
                  <div className="grid w-full max-w-sm items-center gap-1.5">
                    <Label htmlFor="name">
                      <span>{t('tran_upload_image_hint_2')}</span>
                    </Label>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className={cn(
                        'flex mx-auto my-4 size-36 flex-col cursor-pointer hover:opacity-60 transition-opacity items-center justify-center bg-[#F2F6F9] rounded-md py-6 px-8',
                        isFileSizeError && 'outline-offset-7 outline-[1.5px] outline-dashed outline-caution',
                        selectedFile && 'hidden'
                      )}
                    >
                      <PlusIcon className="mb-4" />
                      <div className="text-xs">
                        <span className="text-[#8B97A9]">{t('order_upload_tran_pic')}</span>
                      </div>
                    </button>
                    {selectedFile && (
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        alt="Selected transfer proof"
                        className="max-w-full"
                      />
                    )}
                    {isFileSizeError && (
                      <div className="text-caution text-xs text-center">*{t('tran_image_size_limit')}</div>
                    )}
                    {isFileFormatError && (
                      <div className="text-caution text-xs text-center">*{t('unsupported_file_format')}</div>
                    )}
                  </div>
                </div>
                <div className="text-xs flex items-center justify-center mt-3">
                  <InfoIcon className="size-4 mr-1 inline flex-shrink-0 text-caution" />
                  <span>{t('confirm_all_required_fields_completed')}</span>
                </div>
              </div>
            )}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,text/plain,android/force-camera-workaround"
            className="hidden"
          />
          <DialogFooter className="flex justify-between gap-2">
            <Button className="flex-1" variant="outline" onClick={handleClose}>
              {t('dialog_cancel')}
            </Button>
            {isImageUploaded ? (
              <Button
                className="flex-1 disabled:cursor-not-allowed"
                onClick={handleConfirm}
                disabled={isNameError || !name || !selectedFile || isSubmitting}
              >
                <AnimatePresence>
                  {isSubmitting ? (
                    <LoadingSpinner />
                  ) : (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {t('tran_finish')}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Button>
            ) : (
              <Button className="flex-1" onClick={() => fileInputRef.current?.click()}>
                {t('btn_select_image')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);

export default TransitionImageUploadModal;
